/**
 * LockGate — wraps a gated control (Phase B gating, Jul 2026).
 *
 * When `locked`, it applies the admin-selected visual treatment(s) and
 * intercepts the click (capture phase) so the underlying button never fires —
 * instead it calls `onLockedClick` (which pops the upgrade CTA). When unlocked
 * it renders children untouched (fully transparent, no layout change).
 *
 * `lockStyle` accepts a single style string OR an array of styles that STACK
 * (e.g. frozen sheen + blur + lock badge), per Randy's Jul 2026 request.
 */
import { Lock } from "@phosphor-icons/react";

export function normalizeStyles(lockStyle, lockStyles) {
  const raw = lockStyles ?? lockStyle ?? "red_shimmer";
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.filter(Boolean);
}

export default function LockGate({
  locked,
  lockStyle = "red_shimmer",
  lockStyles,
  onLockedClick,
  block = false,
  className = "",
  badgeSize = 11,
  children,
}) {
  if (!locked) return children;
  const styles = normalizeStyles(lockStyle, lockStyles);
  const styleClasses = styles.map((s) => `cp-lock--${s}`).join(" ");
  const showBadge = styles.includes("lock_badge");
  return (
    <span
      className={`cp-lock ${styleClasses} ${block ? "cp-lock--block" : ""} ${className}`}
      onClickCapture={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onLockedClick?.();
      }}
      role="button"
      aria-disabled="true"
      data-testid="lock-gate"
    >
      {children}
      {showBadge && (
        <span className="cp-lock-badge" aria-hidden="true">
          <Lock size={badgeSize} weight="fill" />
        </span>
      )}
    </span>
  );
}
